import type {
  BalanceResponseBody,
  BillingDisplayRateResponse,
  CreateTopupRequest,
  CreateTopupResponse,
  CreditsResponse,
  GetTopupStatusResponse,
  PricingResponse,
  RedeemGiftCardRequest,
  RedeemGiftCardResponse,
  TransactionsResponse,
  TopupQuoteResponse,
  V1BillingStatementsResponse,
} from '@nadoutong/contracts';
import { CREDIT_LOTS_MAX_LIMIT } from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Typed wrappers around platform-api `/v1/billing/*`.
 *
 * Ledger sign convention (matches public-billing.ts contracts):
 *   - Debits (charges) are negative.
 *   - Credits (gift-card redemptions, topups) are positive.
 *
 * Topup endpoints (`POST /v1/billing/topup`, `GET /v1/billing/topup/:id`)
 * use contracts camelCase shapes. PR P17 consolidated what was previously
 * a drifted local snake_case copy.
 */

const BASE_PATH = '/v1/billing';

export async function getBalance(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<BalanceResponseBody> {
  return platformApiRequest<BalanceResponseBody>(`${BASE_PATH}/balance`, { signal }, fetchImpl);
}

/**
 * The outstanding credit lots behind the balance, in consumption order.
 *
 * Not paginated: an account holds a handful of lots, and the list is a
 * decomposition of one number rather than a ledger that grows forever. The
 * server caps the page; `limit` only narrows it further.
 */
export async function getCredits(
  fetchImpl: typeof fetch,
  query?: { readonly limit?: number },
  signal?: AbortSignal,
): Promise<CreditsResponse> {
  const path =
    query?.limit === undefined
      ? `${BASE_PATH}/credits`
      : `${BASE_PATH}/credits?limit=${String(
          Math.max(1, Math.min(CREDIT_LOTS_MAX_LIMIT, Math.floor(query.limit))),
        )}`;
  return platformApiRequest<CreditsResponse>(path, { signal }, fetchImpl);
}

export interface TransactionsQuery {
  readonly limit?: number;
  readonly offset?: number;
  readonly from?: string;
  readonly to?: string;
}

export async function getTransactions(
  fetchImpl: typeof fetch,
  query?: TransactionsQuery,
  signal?: AbortSignal,
): Promise<TransactionsResponse> {
  const params = new URLSearchParams();
  if (query?.limit !== undefined) {
    params.set('limit', String(Math.max(1, Math.min(100, Math.floor(query.limit)))));
  }
  if (query?.offset !== undefined) {
    params.set('offset', String(Math.max(0, Math.floor(query.offset))));
  }
  if (typeof query?.from === 'string' && query.from.length > 0) {
    params.set('from', query.from);
  }
  if (typeof query?.to === 'string' && query.to.length > 0) {
    params.set('to', query.to);
  }
  const qs = params.toString();
  const path = qs.length > 0 ? `${BASE_PATH}/transactions?${qs}` : `${BASE_PATH}/transactions`;
  return platformApiRequest<TransactionsResponse>(path, { signal }, fetchImpl);
}

export async function getPricing(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<PricingResponse> {
  return platformApiRequest<PricingResponse>(`${BASE_PATH}/pricing/me`, { signal }, fetchImpl);
}

export interface StatementsQuery {
  readonly limit?: number;
  readonly offset?: number;
}

export async function getStatements(
  fetchImpl: typeof fetch,
  query?: StatementsQuery,
  signal?: AbortSignal,
): Promise<V1BillingStatementsResponse> {
  const params = new URLSearchParams();
  if (query?.limit !== undefined) {
    params.set('limit', String(Math.max(1, Math.min(120, Math.floor(query.limit)))));
  }
  if (query?.offset !== undefined) {
    params.set('offset', String(Math.max(0, Math.floor(query.offset))));
  }
  const qs = params.toString();
  const path = qs.length > 0 ? `${BASE_PATH}/statements?${qs}` : `${BASE_PATH}/statements`;
  return platformApiRequest<V1BillingStatementsResponse>(path, { signal }, fetchImpl);
}

export async function getDisplayRate(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<BillingDisplayRateResponse> {
  return platformApiRequest<BillingDisplayRateResponse>(`${BASE_PATH}/display-rate`, { signal }, fetchImpl);
}

export async function redeemGiftCard(
  body: RedeemGiftCardRequest,
  fetchImpl: typeof fetch,
): Promise<RedeemGiftCardResponse> {
  return platformApiRequest<RedeemGiftCardResponse>(
    `${BASE_PATH}/redeem`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function createTopup(
  body: CreateTopupRequest,
  fetchImpl: typeof fetch,
): Promise<CreateTopupResponse> {
  return platformApiRequest<CreateTopupResponse>(
    `${BASE_PATH}/topup`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function getTopupQuote(
  amountCnyCents: number,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<TopupQuoteResponse> {
  const params = new URLSearchParams({ amountCnyCents: String(amountCnyCents) });
  return platformApiRequest<TopupQuoteResponse>(
    `${BASE_PATH}/topup/quote?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function getTopupStatus(
  orderId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetTopupStatusResponse> {
  return platformApiRequest<GetTopupStatusResponse>(
    `${BASE_PATH}/topup/${encodeURIComponent(orderId)}`,
    { signal },
    fetchImpl,
  );
}
